// Customer QR stickers for the 70 x 40 mm label roll the office prints on
// (2026-09-17). One label per printed page: the QR on the left (34 mm, as big
// as the 40 mm height allows) and the customer name + site on the right. Used
// by the QR Stickers page (many at once) and the single Print buttons on the
// customer screen, so every sticker looks the same.

export const LABEL_WIDTH_MM = 70;
export const LABEL_HEIGHT_MM = 40;

const esc = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Long customer names (some run past 60 characters) step the font down rather
// than lose words - the name is what staff match against at the site.
const nameSize = (name) => {
  const len = (name || "").length;
  if (len > 70) return 7.5;
  if (len > 45) return 8.5;
  if (len > 25) return 9.5;
  return 11;
};

// The site label only adds anything when it isn't the default "Main".
const siteLines = ({ siteLabel, siteText }) =>
  [siteLabel && siteLabel.trim().toLowerCase() !== "main" ? siteLabel.trim() : null, siteText?.trim() || null].filter(Boolean);

const labelHtml = (s) => `
  <div class="label">
    <img class="qr" src="${s.dataUrl}" alt="" />
    <div class="txt">
      <div class="brand">TEXAS ALARMS</div>
      <div class="name" style="font-size:${nameSize(s.custName)}pt">${esc(s.custName)}</div>
      ${siteLines(s).map((line) => `<div class="site">${esc(line)}</div>`).join("")}
      <div class="code">${esc([s.branchCode, s.custCode != null ? `#${s.custCode}` : null].filter(Boolean).join(" · "))}</div>
    </div>
  </div>`;

const STYLE = `
  @page { size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .label {
    /* half a millimetre short, so printer rounding never spills onto a second label */
    width: ${LABEL_WIDTH_MM}mm; height: ${LABEL_HEIGHT_MM - 0.5}mm; padding: 2mm 2.5mm 2mm 1.5mm;
    display: flex; align-items: center; gap: 1.5mm; overflow: hidden;
    page-break-after: always; break-after: page; font-family: Arial, Helvetica, sans-serif; color: #000;
  }
  .label:last-child { page-break-after: auto; break-after: auto; }
  .qr { width: 34mm; height: 34mm; flex: 0 0 34mm; image-rendering: pixelated; }
  .txt { flex: 1; min-width: 0; height: 35mm; display: flex; flex-direction: column; justify-content: center; gap: 0.8mm; }
  .brand { font-size: 6.5pt; font-weight: 700; letter-spacing: 0.4pt; color: #7a1f1f; }
  .name {
    font-weight: 700; line-height: 1.12; overflow: hidden; word-break: break-word;
    display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical;
  }
  .site {
    font-size: 6.5pt; line-height: 1.15; overflow: hidden; word-break: break-word;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  }
  .code { font-size: 6pt; color: #444; }
  .wait { font: 14px Arial, sans-serif; padding: 20px; }
`;

// Open the window straight from the click (before any await) so pop-up
// blockers let it through; fill it once the QR images have been fetched.
export const openStickerWindow = (count) => {
  const win = window.open("", "_blank", "width=480,height=420");
  if (!win) return null;
  win.document.write(
    `<html><head><title>QR stickers</title><style>${STYLE}</style></head>` +
      `<body><div class="wait">Preparing ${count} sticker${count === 1 ? "" : "s"}…</div></body></html>`
  );
  win.document.close();
  return win;
};

// stickers: [{ dataUrl, custName, siteLabel, siteText, branchCode, custCode }]
export const writeStickers = (win, stickers) => {
  if (!win || win.closed) return;
  win.document.open();
  win.document.write(
    `<html><head><title>QR stickers (${stickers.length})</title><style>${STYLE}</style></head>` +
      `<body>${stickers.map(labelHtml).join("")}` +
      // Print once every QR image has decoded, so no label comes out blank.
      `<script>
        (function () {
          var imgs = Array.prototype.slice.call(document.images);
          Promise.all(imgs.map(function (i) {
            return i.complete ? Promise.resolve() : new Promise(function (r) { i.onload = i.onerror = r; });
          })).then(function () { setTimeout(function () { window.print(); window.close(); }, 150); });
        })();
      </script></body></html>`
  );
  win.document.close();
};
